import React from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { XCircle } from 'lucide-react';
import { motion } from 'motion/react';

/** Largeur des sidebars (px). Utilisée pour le panneau et le décalage de la table. w-96 = 24rem = 384px. */
export const SIDEBAR_WIDTH_PX = 384;

const SIDEBAR_ANIMATION = {
  initial: { x: SIDEBAR_WIDTH_PX },
  animate: { x: 0 },
  exit: { x: SIDEBAR_WIDTH_PX },
  transition: { duration: 0.2 },
} as const;

const OVERLAY_ANIMATION = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.2 },
} as const;

export interface SidebarProps {
  /** Title or custom header content (e.g. icon + text) */
  title: React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
  /** Show dimmed overlay behind the sidebar (e.g. for alert sidebar) */
  showOverlay?: boolean;
  /** Tailwind z-index class, e.g. 'z-sidebar' or 'z-bulk-edit-sidebar' */
  zIndexClass?: string;
  /** Optional section below the header (e.g. filter count bar) */
  headerExtra?: React.ReactNode;
  /** Optional class for the scrollable content area */
  contentClassName?: string;
}

export function Sidebar({
  title,
  onClose,
  children,
  showOverlay = false,
  zIndexClass = 'z-sidebar',
  headerExtra,
  contentClassName,
}: SidebarProps) {
  return (
    <>
      {showOverlay && (
        <motion.div
          key="sidebar-overlay"
          {...OVERLAY_ANIMATION}
          onClick={onClose}
          className={cn('fixed inset-0 bg-black/30', zIndexClass)}
          aria-hidden
        />
      )}
      <motion.div
        key="sidebar-panel"
        {...SIDEBAR_ANIMATION}
        style={{ width: SIDEBAR_WIDTH_PX }}
        className={cn(
          'fixed right-0 top-0 flex h-full flex-col border-l border-gray-200 bg-white shadow-lg',
          zIndexClass
        )}
      >
        <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-200 p-4">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            {typeof title === 'string' ? (
              <h3 className="truncate text-lg font-medium">{title}</h3>
            ) : (
              title
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-8 w-8 shrink-0 p-0"
            aria-label="Fermer"
          >
            <XCircle className="h-4 w-4" />
          </Button>
        </div>

        {headerExtra != null ? (
          <div className="flex-shrink-0 border-b border-gray-200 bg-gray-50 px-4 py-3">
            {headerExtra}
          </div>
        ) : null}

        <div className={cn('flex-1 overflow-y-auto p-4', contentClassName)}>
          {children}
        </div>
      </motion.div>
    </>
  );
}
